import { useState, useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { createColumnHelper, type SortingState } from "@tanstack/react-table";
import { type components as AdminComponents } from "@njbsoft/types/admin";
import { useAdminEmails, type AdminEmailsParams } from "@/hooks/use-admin-emails";
import { useDebounce } from "@/hooks/use-debounce";
import { DataTable } from "@/components/data-table";
import { Pagination } from "@/components/pagination";
import { StatusBadge } from "@/components/status-badge";
import { formatDateTime } from "@njbsoft/utils";

type EmailSummary = AdminComponents["schemas"]["AdminEmailSummary"];

export const Route = createFileRoute("/_admin/emails/")({
  component: EmailLogsPage,
});

const PAGE_SIZE = 20;

const columnHelper = createColumnHelper<EmailSummary>();

function EmailLogsPage() {
  const navigate = useNavigate();

  // Filter state
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([{ id: "created_at", desc: true }]);

  const debouncedSearch = useDebounce(search, 300);

  // Build query params
  const params: AdminEmailsParams = useMemo(() => {
    const p: AdminEmailsParams = {
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      sort: "created_at",
      order: sorting.length > 0 && sorting[0] && !sorting[0].desc ? "asc" : "desc",
    };
    if (debouncedSearch) p.search = debouncedSearch;
    if (statusFilter !== "all") p.status = statusFilter;
    if (categoryFilter !== "all") p.category = categoryFilter;
    return p;
  }, [debouncedSearch, statusFilter, categoryFilter, page, sorting]);

  const { data, isLoading } = useAdminEmails(params);

  const columns = useMemo(
    () => [
      columnHelper.accessor("recipient_email", {
        header: "Recipient",
        enableSorting: false,
      }),
      columnHelper.accessor("subject", {
        header: "Subject",
        enableSorting: false,
      }),
      columnHelper.accessor("template_name", {
        header: "Template",
        enableSorting: false,
      }),
      columnHelper.accessor("status", {
        header: "Status",
        enableSorting: false,
        cell: (info) => <StatusBadge status={info.getValue()} type="email" />,
      }),
      columnHelper.accessor("category", {
        header: "Category",
        enableSorting: false,
        cell: (info) => <span className="capitalize">{info.getValue()}</span>,
      }),
      columnHelper.accessor("created_at", {
        header: "Sent At",
        enableSorting: true,
        cell: (info) => formatDateTime(info.getValue()),
      }),
    ],
    [],
  );

  const totalPages = Math.ceil((data?.total ?? 0) / PAGE_SIZE);

  function handleRowClick(email: EmailSummary) {
    void navigate({ to: "/emails/$emailId", params: { emailId: email.id } });
  }

  // Reset page when filters change
  function handleSearchChange(value: string) {
    setSearch(value);
    setPage(1);
  }

  function handleStatusChange(value: string) {
    setStatusFilter(value);
    setPage(1);
  }

  function handleCategoryChange(value: string) {
    setCategoryFilter(value);
    setPage(1);
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-slate-900">Email Logs</h1>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Search by recipient email..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />

        <select
          value={statusFilter}
          onChange={(e) => handleStatusChange(e.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="sent">Sent</option>
          <option value="delivered">Delivered</option>
          <option value="bounced">Bounced</option>
          <option value="failed">Failed</option>
          <option value="opened">Opened</option>
          <option value="clicked">Clicked</option>
          <option value="complained">Complained</option>
          <option value="delivery_delayed">Delivery Delayed</option>
        </select>

        <select
          value={categoryFilter}
          onChange={(e) => handleCategoryChange(e.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="all">All Categories</option>
          <option value="system">System</option>
          <option value="marketing">Marketing</option>
          <option value="notification">Notification</option>
        </select>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={data?.emails ?? []}
        sorting={sorting}
        onSortingChange={setSorting}
        isLoading={isLoading}
        emptyMessage="No emails found"
        onRowClick={handleRowClick}
      />

      {/* Pagination */}
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalItems={data?.total ?? 0}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />
    </div>
  );
}
